export type Language = 'auto' | 'ar' | 'be' | 'bg' | 'bs' | 'ca' | 'cs' | 'da' | 'de' | 'el' | 'en' | 'es' | 'et' | 'fi' | 'fr' | 'hi' | 'hr' | 'hu' | 'it' | 'ko' | 'lb' | 'lt' | 'lv' | 'mk' | 'nl' | 'no' | 'pl' | 'pt' | 'ro' | 'ru' | 'sk' | 'sl' | 'sq' | 'sr' | 'sv' | 'tr' | 'uk' | 'zh';

export interface NamedTranslation {
  language: string,
  boxStart: string,
  boxInProgress: string,
  boxCompleted: string,
  endPrivacyPolicy: string,
  ariaLabelStart?: string,
  ariaLabelRunning?: string,
  ariaLabelDone?: string,
  srRunning?: string,
  srDone?: string,
  srFailed?: string,
  srTrustcaptcha?: string,
  srPrivacy?: string,
}

function mapTranslation(currentLanguage: string, array: readonly string[]): NamedTranslation {
  return {
    language: currentLanguage,
    boxStart: array[0],
    boxInProgress: array[1],
    boxCompleted: array[2],
    endPrivacyPolicy: array[3],
    ariaLabelStart: (array[4] || translations['en'][4]),
    ariaLabelRunning: (array[5] || translations['en'][5]),
    ariaLabelDone: (array[6] || translations['en'][6]),
    srRunning: (array[7] || translations['en'][7]),
    srDone: (array[8] || translations['en'][8]),
    srFailed: (array[9] || translations['en'][9]),
    srTrustcaptcha: (array[10] || translations['en'][10]),
    srPrivacy: (array[11] || translations['en'][11]),
  };
}

function mergeTranslations(defaultTranslation: NamedTranslation, customTranslation: Partial<NamedTranslation>): NamedTranslation {
  return {
    ...defaultTranslation,
    ...customTranslation
  };
}

export function getTranslation(language: string = 'auto', customTranslations: string = ''): NamedTranslation {

  let filteredCustomTranslations = []
  if (customTranslations && customTranslations != '') {
    try {
      filteredCustomTranslations = JSON.parse(customTranslations).filter(it => it.language && it.language !== '')
    } catch (e) {
      // nothing
    }
  }
  let selectedLanguage = language;

  if (language === 'auto') {
    const foundLanguage = document.documentElement.lang.trim().toLowerCase().substring(0, 2);
    if (foundLanguage && translations[foundLanguage]) {
      selectedLanguage = foundLanguage;
    } else if (foundLanguage && filteredCustomTranslations.find(it => it.language === foundLanguage)) {
      selectedLanguage = foundLanguage;
    } else {
      selectedLanguage = 'en';
    }
  }

  const translationArray = translations[selectedLanguage] || translations['en'];
  const translation = mapTranslation(selectedLanguage, translationArray);
  const customTranslation = filteredCustomTranslations.find(it => it.language === selectedLanguage)
  if (customTranslation) {
    return mergeTranslations(translation, customTranslation)
  }

  return translation
}

const translations: Translations = {
  // Arabisch
  ar: [
    "ابدأ التحقق",
    "التحقق جارٍ",
    "أنا إنسان!",
    "سياسة الخصوصية",
    "اضغط الزر حتى يصبح أخضر تماماً",
    "الآن اترك الزر",
    "ناجح",
    "تم تحرير الزر مبكرًا جدًا أو متأخرًا جدًا",
    "استمر في الضغط",
  ],
  // Weißrussisch
  be: [
    "Пачаць праверку",
    "Праверка працягваецца",
    "Я чалавек!",
    "Палітыка прыватнасці",
  ],
  // Bulgarisch
  bg: [
    "Старт на проверката",
    "Проверката е в ход",
    "Аз съм човек!",
    "Защита на данни",
  ],
  // Bosnisch
  bs: [
    "Započni verifikaciju",
    "Verifikacija je u toku",
    "Ja sam čovjek!",
    "Privatnost",
  ],
  // Katalanisch
  ca: [
    "Comença la verificació",
    "Verificació en curs",
    "Sóc humà!",
    "Privacitat",
  ],
  // Tschechisch
  cs: [
    "Začít kontrolu",
    "Kontrola probíhá",
    "Jsem člověk!",
    "Soukromí",
  ],
  // Dänisch
  da: [
    "Start kontrol",
    "Kontrol i gang",
    "Jeg er et menneske!",
    "Privatliv",
  ],
  // Deutsch
  de: [
    "Überprüfung starten",
    "Überprüfung läuft",
    "Ich bin ein Mensch!",
    "Datenschutz",
    "CAPTCHA nicht gestartet, klicken Sie hier, um das CAPTCHA zu starten",
    "CAPTCHA läuft",
    "CAPTCHA abgeschlossen",
    "CAPTCHA-Prüfung gestartet",
    "CAPTCHA-Prüfung erfolgreich abgeschlossen",
    "CAPTCHA ist aufgrund eines technischen Fehlers fehlgeschlagen. Bitte wenden Sie sich an den Support.",
    "Zur Trustcaptcha-Website gehen",
    "Zur Datenschutzerklärung gehen"
  ],
  // Griechisch
  el: [
    "Έναρξη ελέγχου",
    "Ο έλεγχος βρίσκεται σε εξέλιξη",
    "Είμαι άνθρωπος!",
    "Απόρρητο",
  ],
  // Englisch
  en: [
    "Start verification",
    "Verification in progress",
    "I am a human!",
    "Privacy",
    "CAPTCHA not started, click here to start the CAPTCHA",
    "CAPTCHA in process",
    "CAPTCHA completed",
    "CAPTCHA verification started",
    "CAPTCHA verification successfully completed",
    "CAPTCHA has failed due to a technical error. Please contact the support.",
    "Go to the Trustcaptcha website",
    "Go to the privacy policy"
  ],
  // Spanisch
  es: [
    "Iniciar verificación",
    "Verificación en curso",
    "¡Soy humano!",
    "Privacidad",
    "CAPTCHA no iniciado; haga clic aquí para iniciar el CAPTCHA",
    "CAPTCHA en curso",
    "CAPTCHA completado",
    "Verificación CAPTCHA iniciada",
    "Verificación CAPTCHA completada con éxito",
    "El CAPTCHA ha fallado debido a un error técnico. Por favor, póngase en contacto con el soporte.",
    "Ir al sitio web de Trustcaptcha",
    "Ir a la política de privacidad"
  ],
  // Estnisch
  et: [
    "Alusta kontrollimist",
    "Kontroll on käimas.",
    "Ma olen inimene!",
    "Privaatsus",
  ],
  // Finnisch
  fi: [
    "Aloita tarkistus",
    "Tarkistus käynnissä",
    "Olen ihminen!",
    "Yksityisyys",
  ],
  // Französisch
  fr: [
    "Démarrer la vérification",
    "Vérification en cours",
    "Je suis un humain !",
    "Confidentialité",
    "CAPTCHA non démarré ; cliquez ici pour lancer le CAPTCHA",
    "CAPTCHA en cours d’exécution",
    "CAPTCHA terminé",
    "Vérification CAPTCHA lancée",
    "Vérification CAPTCHA réussie",
    "Le CAPTCHA a échoué en raison d’une erreur technique. Veuillez contacter le support.",
    "Aller sur le site Trustcaptcha",
    "Aller à la politique de confidentialité"
  ],
  // Hindi
  hi: [
    "सत्यापन प्रारंभ करें",
    "सत्यापन प्रगति पर है",
    "मैं एक इंसान हूँ!",
    "गोपनीयता",
  ],
  // Kroatisch
  hr: [
    "Pokreni provjeru",
    "Provjera je u tijeku",
    "Ja sam čovjek!",
    "Privatnost",
  ],
  // Ungarisch
  hu: [
    "Indítsa el az ellenőrzést",
    "Ellenőrzés folyamatban",
    "Ember vagyok!",
    "Adatvédelem",
  ],
  // Italienisch
  it: [
    "Avvia verifica",
    "Verifica in corso",
    "Sono umano!",
    "Privacy",
    "CAPTCHA non avviato; fai clic qui per avviare il CAPTCHA",
    "CAPTCHA in esecuzione",
    "CAPTCHA completato",
    "Verifica CAPTCHA avviata",
    "Verifica CAPTCHA completata con successo",
    "Il CAPTCHA non è riuscito a causa di un errore tecnico. Contatta il supporto.",
    "Vai al sito Trustcaptcha",
    "Vai all'informativa sulla privacy"
  ],
  // Koreanisch
  ko: [
    "인증 시작",
    "인증 진행 중",
    "나는 사람입니다!",
    "개인 정보 보호",
  ],
  // Luxemburgisch
  lb: [
    "Verifikatioun starten",
    "Verifikatioun am Gaang",
    "Ech sinn e Mënsch!",
    "Dateschutz",
    "CAPTCHA net gestart; klick hei fir de CAPTCHA ze starten",
    "CAPTCHA leeft",
    "CAPTCHA ofgeschloss",
    "CAPTCHA-Verifikatioun ugefaang",
    "CAPTCHA-Verifikatioun erfollegräich ofgeschloss",
    "De CAPTCHA ass wéinst engem technesche Feeler fehlgeschloen. Kontaktéiert de Support.",
    "Trustcaptcha besichen",
    "Dateschutzerklärung besichen"
  ],
  // Litauisch
  lt: [
    "Pradėti patikrą",
    "Patikra vyksta",
    "Aš esu žmogus!",
    "Privatumas",
  ],
  // Lettisch
  lv: [
    "Sākt pārbaudi",
    "Pārbaude notiek",
    "Es esmu cilvēks!",
    "Privātums",
  ],
  // Mazedonisch
  mk: [
    "Започнете верификација",
    "Верификацијата е во тек",
    "Јас сум човек!",
    "Приватност",
  ],
  // Niederländisch
  nl: [
    "Verificatie starten",
    "Verificatie bezig",
    "Ik ben een mens!",
    "Privacy",
    "CAPTCHA niet gestart, klik hier om de CAPTCHA te starten",
    "CAPTCHA in uitvoering",
    "CAPTCHA voltooid",
    "CAPTCHA-verificatie gestart",
    "CAPTCHA-verificatie succesvol voltooid",
    "De CAPTCHA is mislukt door een technische fout. Neem contact op met de support.",
    "Ga naar de Trustcaptcha-website",
    "Ga naar het privacybeleid"
  ],
  // Norwegisch
  no: [
    "Start verifisering",
    "Verifisering pågår",
    "Jeg er et menneske!",
    "Personvern",
  ],
  // Polnisch
  pl: [
    "Rozpocznij weryfikację",
    "Weryfikacja trwa",
    "Jestem człowiekiem!",
    "Prywatność",
  ],
  // Portugiesisch
  pt: [
    "Iniciar verificação",
    "Verificação em progresso",
    "Sou humano!",
    "Privacidade",
  ],
  // Rumänisch
  ro: [
    "Începeți verificarea",
    "Verificarea este în desfășurare",
    "Sunt om!",
    "Confidențialitate",
  ],
  // Russisch
  ru: [
    "Начать проверку",
    "Проверка выполняется",
    "Я человек!",
    "Политика конфиденциальности",
  ],
  // Slowakisch
  sk: [
    "Začať kontrolu",
    "Kontrola prebieha",
    "Som človek!",
    "Súkromie",
  ],
  // Slowenisch
  sl: [
    "Začetek preverjanja",
    "Preverjanje poteka",
    "Sem človek!",
    "Zasebnost",
  ],
  // Albanisch
  sq: [
    "Filloni verifikimin",
    "Verifikimi në vazhdim",
    "Unë jam një njeri!",
    "Privatësia",
  ],
  // Serbisch
  sr: [
    "Započni verifikaciju",
    "Verifikacija u toku",
    "Ja sam čovek!",
    "Privatnost",
  ],
  // Schwedisch
  sv: [
    "Starta kontroll",
    "Kontroll pågår",
    "Jag är en människa!",
    "Sekretess",
  ],
  // Türkisch
  tr: [
    "Doğrulamayı başlat",
    "Doğrulama devam ediyor",
    "Ben bir insanım!",
    "Gizlilik",
  ],
  // Ukrainisch
  uk: [
    "Почати перевірку",
    "Виконується перевірка",
    "Я людина!",
    "Конфіденційність",
  ],
  // Chinesisch (Mandarin)
  zh: [
    "开始验证",
    "验证进行中",
    "我是人类！",
    "隐私",
  ]
}


export interface Translations {
  [languageCode: string]: Translation;
}

export type Translation = [
  boxStart: string,
  boxInProgress: string,
  boxCompleted: string,
  endPrivacyPolicy: string,
  ariaLabelStart?: string,
  ariaLabelRunning?: string,
  ariaLabelDone?: string,
  srRunning?: string,
  srDone?: string,
  srFailed?: string,
  srTrustcaptcha?: string,
  srPrivacy?: string,
];
